import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Plus, Search, Phone, MessageSquare, MapPin, Trash2, Edit, FileText, ClipboardList, AlertTriangle, ChevronDown, Calendar, DollarSign, Eye, Wand2, Filter, Check, PinIcon, MoreHorizontal, Ruler } from "lucide-react";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import CustomerDialog from "@/components/CustomerDialog";
import JobDialog from "@/components/JobDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDate } from "@/utils/date";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Customer status constants
const CUSTOMER_STATUSES = {
  lead: { label: "Lead", color: "bg-blue-100 text-blue-700", description: "New potential customer" },
  scheduled: { label: "Scheduled", color: "bg-amber-100 text-amber-700", description: "Has an appointment booked" },
  customer_paid: { label: "Customer", color: "bg-green-100 text-green-700", description: "Completed job, paid" },
  do_not_contact: { label: "Do Not Contact", color: "bg-red-100 text-red-700", description: "No communications allowed" },
};

export default function Customers() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [customers, setCustomers] = useState([]);
  const [statsCounts, setStatsCounts] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteCustomerId, setDeleteCustomerId] = useState(null);
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Status change state
  const [statusChangeCustomer, setStatusChangeCustomer] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [showDNCConfirm, setShowDNCConfirm] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  // Reconcile statuses dialog
  const [showReconcile, setShowReconcile] = useState(false);
  const [reconcilePreview, setReconcilePreview] = useState(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  
  // View dialogs for customer data
  const [showEstimatesDialog, setShowEstimatesDialog] = useState(false);
  const [showInvoicesDialog, setShowInvoicesDialog] = useState(false);
  const [showJobsDialog, setShowJobsDialog] = useState(false);
  const [customerEstimates, setCustomerEstimates] = useState([]);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [customerJobs, setCustomerJobs] = useState([]);
  const [loadingCustomerData, setLoadingCustomerData] = useState(false);
  const [backfillingZips, setBackfillingZips] = useState(false);

  const handleBackfillZips = async () => {
    if (backfillingZips) return;
    setBackfillingZips(true);
    try {
      const token = (await import("@/utils/auth")).getSessionToken();
      const { data } = await axios.post(`${API}/customers/backfill-zips`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.updated > 0) {
        toast.success(`Backfilled ZIPs on ${data.updated} of ${data.scanned} customer(s). Warming ${data.unique_zips} unique ZIP centroids in the background.`);
      } else {
        toast.success(`Scanned ${data.scanned} customer(s) — all ZIPs already stored.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to backfill ZIPs");
    } finally {
      setBackfillingZips(false);
    }
  };

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAuthenticated(true);
          setUser(response.data);
        } else {
          setIsAuthenticated(false);
          navigate('/login');
        }
      } catch (error) {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCustomers();
      fetchStats();
    }
  }, [isAuthenticated]);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/customers`, {
        params: searchQuery ? { search: searchQuery } : {},
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to fetch customers");
    }
  };

  // Server-side per-status counts — sourced from the full customers
  // collection so the filter pills stay accurate beyond the list cap.
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/customers/stats`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setStatsCounts(response.data?.counts || null);
    } catch (error) {
      // Non-fatal — pills will fall back to client-side counts.
      console.error("Failed to fetch customer stats:", error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      const timer = setTimeout(() => {
        fetchCustomers();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchQuery]);

  // Refresh both the list and the server-side counts in one call so KPI
  // pills stay in sync after any mutation (status change, delete, etc.).
  const refreshCustomers = () => {
    fetchCustomers();
    fetchStats();
  };

  const handleOpenReconcile = async () => {
    setShowReconcile(true);
    setReconcilePreview(null);
    setReconcileLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/admin/reconcile-customer-statuses?dry_run=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReconcilePreview(response.data);
    } catch (error) {
      toast.error("Failed to load preview");
      setShowReconcile(false);
    } finally {
      setReconcileLoading(false);
    }
  };

  const handleConfirmReconcile = async () => {
    setReconcileLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/admin/reconcile-customer-statuses?dry_run=false`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const r = response.data;
      toast.success(
        `Reconciled: ${r.promoted_to_customer_paid} promoted, ${r.demoted_to_lead} demoted`
      );
      setShowReconcile(false);
      refreshCustomers();
    } catch (error) {
      toast.error("Failed to reconcile statuses");
    } finally {
      setReconcileLoading(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!deleteCustomerId) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/customers/${deleteCustomerId}`, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Customer deleted");
      setDeleteCustomerId(null);
      refreshCustomers();
    } catch (error) {
      toast.error("Failed to delete customer");
    }
  };

  const handleStatusChange = async () => {
    if (!statusChangeCustomer || !newStatus) return;
    
    // If changing to DNC, show confirmation first
    if (newStatus === "do_not_contact" && !showDNCConfirm) {
      setShowDNCConfirm(true);
      return;
    }
    
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(
        `${API}/customers/${statusChangeCustomer.customer_id}/status`,
        { status: newStatus, note: statusNote || null },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      toast.success(`Status updated to ${CUSTOMER_STATUSES[newStatus]?.label || newStatus}`);
      setStatusChangeCustomer(null);
      setNewStatus("");
      setStatusNote("");
      setShowDNCConfirm(false);
      refreshCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const openMaps = (address) => {
    const encoded = encodeURIComponent(address);
    const ua = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/.test(ua)) {
      window.location.href = `maps://?q=${encoded}`;
    } else if (/Android/i.test(ua)) {
      window.location.href = `geo:0,0?q=${encoded}`;
    } else {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encoded}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const openPhone = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  // Fetch customer's jobs
  const fetchCustomerJobs = async (customer) => {
    setSelectedCustomer(customer);
    setLoadingCustomerData(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/jobs?customer_id=${customer.customer_id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCustomerJobs(response.data || []);
      setShowJobsDialog(true);
    } catch (error) {
      setCustomerJobs([]);
      setShowJobsDialog(true);
    } finally {
      setLoadingCustomerData(false);
    }
  };

  // Fetch customer's estimates
  const fetchCustomerEstimates = async (customer) => {
    setSelectedCustomer(customer);
    setLoadingCustomerData(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/estimates?customer_id=${customer.customer_id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCustomerEstimates(response.data || []);
      setShowEstimatesDialog(true);
    } catch (error) {
      setCustomerEstimates([]);
      setShowEstimatesDialog(true);
    } finally {
      setLoadingCustomerData(false);
    }
  };

  // Fetch customer's invoices
  const fetchCustomerInvoices = async (customer) => {
    setSelectedCustomer(customer);
    setLoadingCustomerData(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/invoices?customer_id=${customer.customer_id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCustomerInvoices(response.data || []);
      setShowInvoicesDialog(true);
    } catch (error) {
      setCustomerInvoices([]);
      setShowInvoicesDialog(true);
    } finally {
      setLoadingCustomerData(false);
    }
  };

  const openSMS = (phone) => {
    window.location.href = `sms:${phone}`;
  };

  const getStatusBadge = (status) => {
    const statusInfo = CUSTOMER_STATUSES[status] || CUSTOMER_STATUSES.lead;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  // Filter customers by status
  const filteredCustomers = customers.filter(c => {
    if (statusFilter === "all") return true;
    return (c.status || "lead") === statusFilter;
  });

  // Count by status — prefer server-side aggregation (full collection,
  // no row cap). Fall back to client-side reduce over the loaded list while
  // the stats call is in-flight or if it fails.
  const statusCounts = statsCounts || customers.reduce((acc, c) => {
    const status = c.status || "lead";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  const totalCustomerCount = statsCounts ? statsCounts.total : customers.length;

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header user={user} />
      
      <div className="max-w-md mx-auto pb-24 p-4">
        <div className="mb-6" data-testid="customers-header">
          <div className="flex items-center justify-between mb-4 gap-2">
            <h1 className="font-barlow text-4xl font-bold text-zinc-950 uppercase">Customers</h1>
            <div className="flex items-center gap-2">
              {/* Compact status filter — replaces the old pill row to save
                  vertical space. Shows the active status + count; opens a
                  popover with every status option (each with its count). */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    data-testid="customers-status-filter-trigger"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs font-medium transition-colors"
                    aria-label="Filter customers by status"
                  >
                    <Filter className="w-3.5 h-3.5" />
                    <span className="truncate max-w-[110px]">
                      {statusFilter === "all"
                        ? `All (${totalCustomerCount})`
                        : `${CUSTOMER_STATUSES[statusFilter]?.label || statusFilter} (${statusCounts[statusFilter] || 0})`}
                    </span>
                    <ChevronDown className="w-3 h-3 opacity-60" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56 p-1">
                  <button
                    onClick={() => setStatusFilter("all")}
                    data-testid="customers-filter-option-all"
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      statusFilter === "all"
                        ? "bg-zinc-900 text-white"
                        : "hover:bg-zinc-100 text-zinc-700"
                    }`}
                  >
                    <span>All</span>
                    <span className="flex items-center gap-2">
                      <span className="text-xs opacity-70">{totalCustomerCount}</span>
                      {statusFilter === "all" && <Check className="w-3.5 h-3.5" />}
                    </span>
                  </button>
                  {Object.entries(CUSTOMER_STATUSES).map(([key, info]) => (
                    <button
                      key={key}
                      onClick={() => setStatusFilter(key)}
                      data-testid={`customers-filter-option-${key}`}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        statusFilter === key
                          ? "bg-zinc-900 text-white"
                          : "hover:bg-zinc-100 text-zinc-700"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${info.color.split(" ")[0].replace("100", "500")}`}></span>
                        {info.label}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="text-xs opacity-70">{statusCounts[key] || 0}</span>
                        {statusFilter === key && <Check className="w-3.5 h-3.5" />}
                      </span>
                    </button>
                  ))}
                </PopoverContent>
              </Popover>

              {user?.role === "Admin" && (
                <button
                  onClick={handleOpenReconcile}
                  title="Clean up customer statuses"
                  data-testid="reconcile-statuses-button"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs font-medium transition-colors"
                >
                  <Wand2 className="w-3.5 h-3.5" />
                  Clean Up
                </button>
              )}
              {user?.role === "Admin" && (
                <button
                  onClick={handleBackfillZips}
                  disabled={backfillingZips}
                  title="Extract missing ZIP codes from stored addresses"
                  data-testid="backfill-zips-button"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs font-medium transition-colors disabled:opacity-60"
                >
                  <PinIcon className="w-3.5 h-3.5" />
                  {backfillingZips ? "Backfilling…" : "Backfill ZIPs"}
                </button>
              )}
            </div>
          </div>

          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="customer-search-input"
              className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base pl-12 pr-4 bg-white font-inter"
            />
          </div>
        </div>

        <div className="space-y-4" data-testid="customers-list">
          {filteredCustomers.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-inter text-zinc-500">No customers found</p>
            </div>
          ) : (
            filteredCustomers.map((customer) => {
              const isDNC = customer.status === "do_not_contact";
              
              return (
                <div
                  key={customer.customer_id}
                  data-testid={`customer-card-${customer.customer_id}`}
                  className={`bg-white rounded-xl border-2 shadow-sm p-5 ${
                    isDNC ? "border-red-200 bg-red-50/30" : "border-zinc-100"
                  }`}
                >
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-inter text-lg font-bold text-zinc-900" data-testid="customer-name">
                          {customer.fullName}
                        </h3>
                        {/* Repeat Customer Badge */}
                        {customer.is_repeat && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full" data-testid="repeat-badge">
                            Repeat
                          </span>
                        )}
                        {customer.sms_opt_out && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-full" data-testid="sms-optout-badge"
                            title={customer.sms_opt_out_at ? `Replied STOP ${new Date(customer.sms_opt_out_at).toLocaleDateString()}` : "Replied STOP"}>
                            Texts: opted out
                          </span>
                        )}
                      </div>
                      
                      {/* Status Badge with Dropdown for Admins */}
                      {user?.role === "Admin" ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button 
                              className="flex items-center gap-1 cursor-pointer hover:opacity-80"
                              data-testid="status-dropdown-trigger"
                            >
                              {getStatusBadge(customer.status || "lead")}
                              <ChevronDown className="w-3 h-3" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-48 p-2" align="end">
                            <div className="space-y-1">
                              {Object.entries(CUSTOMER_STATUSES).map(([key, info]) => (
                                <button
                                  key={key}
                                  onClick={() => {
                                    setStatusChangeCustomer(customer);
                                    setNewStatus(key);
                                    if (key === "do_not_contact") {
                                      setShowDNCConfirm(true);
                                    } else {
                                      // Direct change for non-DNC
                                      const token = localStorage.getItem('session_token');
                                      axios.put(
                                        `${API}/customers/${customer.customer_id}/status`,
                                        { status: key },
                                        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
                                      ).then(() => {
                                        toast.success(`Status updated to ${info.label}`);
                                        refreshCustomers();
                                      }).catch(err => {
                                        toast.error(err.response?.data?.detail || "Failed to update status");
                                      });
                                    }
                                  }}
                                  className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-zinc-100 ${
                                    (customer.status || "lead") === key ? "bg-zinc-100 font-medium" : ""
                                  }`}
                                >
                                  <span className={`inline-block w-2 h-2 rounded-full mr-2 ${info.color.split(" ")[0]}`}></span>
                                  {info.label}
                                </button>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        getStatusBadge(customer.status || "lead")
                      )}
                    </div>
                    
                    <p className="text-sm text-zinc-600" data-testid="customer-phone">{customer.phoneNumber}</p>
                    {customer.email && (
                      <p className="text-sm text-zinc-500">{customer.email}</p>
                    )}
                    {customer.created_at && (
                      <p className="text-xs text-zinc-400 mt-1" data-testid="customer-added-date">
                        Added {formatDate(customer.created_at)}
                      </p>
                    )}
                    {customer.address && customer.address !== "Not provided yet" && (
                      <div className="flex items-start gap-2 mt-2">
                        <MapPin className="w-4 h-4 text-[#207024] mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-zinc-600" data-testid="customer-address">{customer.address}</p>
                      </div>
                    )}
                    {customer.notes && (
                      <p className="text-sm text-zinc-500 mt-2 italic" data-testid="customer-notes">{customer.notes}</p>
                    )}
                    {customer.last_inbound_message && (
                      <p className="text-xs text-zinc-600 mt-2 bg-zinc-50 rounded-md px-2 py-1.5" data-testid="customer-last-reply">
                        <span className="font-semibold">Last reply{customer.last_inbound_at ? ` (${new Date(customer.last_inbound_at).toLocaleDateString()})` : ""}:</span>{" "}
                        {customer.last_inbound_message}
                      </p>
                    )}
                    {customer.source && (
                      <div className="mt-2 flex items-center gap-1" data-testid="customer-source">
                        <span className="text-xs font-medium text-amber-600">Source:</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          customer.source === "Google Ads" ? "bg-blue-100 text-blue-700" :
                          customer.source === "Facebook" ? "bg-indigo-100 text-indigo-700" :
                          customer.source === "Rosie Call" ? "bg-orange-100 text-orange-700" :
                          customer.source === "Manual" ? "bg-zinc-100 text-zinc-600" :
                          "bg-amber-100 text-amber-700"
                        }`}>
                          {customer.source}
                        </span>
                      </div>
                    )}
                    
                    {/* Repeat Customer Stats */}
                    {(customer.paid_invoice_count > 0 || customer.active_estimate_count > 0) && (
                      <div className="mt-3 flex items-center gap-4 text-sm" data-testid="customer-stats">
                        {customer.paid_invoice_count > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-zinc-500">Paid Invoices:</span>
                            <span className="font-semibold text-zinc-700">{customer.paid_invoice_count}</span>
                          </div>
                        )}
                        {customer.total_revenue > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-zinc-500">Revenue:</span>
                            <span className="font-semibold text-green-600">${customer.total_revenue.toFixed(2)}</span>
                          </div>
                        )}
                        {customer.active_estimate_count > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-zinc-500">Active Estimates:</span>
                            <span className="font-semibold text-amber-600">{customer.active_estimate_count}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* DNC Warning */}
                    {isDNC && (
                      <div className="mt-3 flex items-center gap-2 text-red-600 bg-red-100 px-3 py-2 rounded-lg">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm font-medium">Do Not Contact - No communications allowed</span>
                      </div>
                    )}
                  </div>

                  {/* Action buttons — 3 inline primaries (Call, Message,
                      Jobs) + "More" popover with everything secondary
                      (Map, Estimates, Invoices, Edit, Delete). Same
                      compaction pattern as Estimates cards. Call &
                      Message are disabled when the customer is DNC. */}
                  <div className="flex gap-2 items-center">
                    <button
                      onClick={() => !isDNC && openPhone(customer.phoneNumber)}
                      disabled={isDNC}
                      data-testid="customer-call-button"
                      className={`h-10 px-3 rounded-lg font-semibold flex items-center justify-center gap-1.5 border flex-1 text-sm ${
                        isDNC
                          ? "bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed"
                          : "bg-zinc-100 hover:bg-zinc-200 text-[#207024] border-zinc-200"
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      Call
                    </button>
                    <button
                      onClick={() => !isDNC && navigate('/messages', { state: { customerId: customer.customer_id } })}
                      disabled={isDNC}
                      data-testid="customer-message-button"
                      className={`h-10 px-3 rounded-lg font-semibold flex items-center justify-center gap-1.5 border flex-1 text-sm ${
                        isDNC
                          ? "bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed"
                          : "bg-zinc-100 hover:bg-zinc-200 text-[#207024] border-zinc-200"
                      }`}
                    >
                      <MessageSquare className="w-4 h-4" />
                      Message
                    </button>
                    <button
                      onClick={() => fetchCustomerJobs(customer)}
                      data-testid="view-jobs-from-customer"
                      className="h-10 px-3 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold flex-1 text-sm"
                    >
                      Jobs
                    </button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          data-testid={`customer-more-${customer.customer_id}`}
                          className="h-10 px-2.5 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-semibold flex items-center gap-1 border border-zinc-200 text-sm"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-52 p-1">
                        <button
                          onClick={() => openMaps(customer.address)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-left text-zinc-700 hover:bg-zinc-100"
                        >
                          <MapPin className="w-4 h-4 shrink-0" />
                          Map
                        </button>
                        <button
                          onClick={() => fetchCustomerEstimates(customer)}
                          data-testid="view-estimates-from-customer"
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-left text-zinc-700 hover:bg-zinc-100"
                        >
                          <ClipboardList className="w-4 h-4 shrink-0" />
                          Estimates
                        </button>
                        <button
                          onClick={() => fetchCustomerInvoices(customer)}
                          data-testid="view-invoices-from-customer"
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-left text-zinc-700 hover:bg-zinc-100"
                        >
                          <FileText className="w-4 h-4 shrink-0" />
                          Invoices
                        </button>
                        <button
                          onClick={() => navigate(`/customers/${customer.customer_id}/measurements`)}
                          data-testid={`view-saved-measurements-${customer.customer_id}`}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-left text-zinc-700 hover:bg-zinc-100"
                        >
                          <Ruler className="w-4 h-4 shrink-0" />
                          Saved Measurements
                        </button>
                        <button
                          onClick={() => {
                            setEditingCustomer(customer);
                            setShowCustomerDialog(true);
                          }}
                          data-testid="edit-customer-button"
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-left text-zinc-700 hover:bg-zinc-100"
                        >
                          <Edit className="w-4 h-4 shrink-0" />
                          Edit
                        </button>
                        <div className="my-1 border-t border-zinc-200" />
                        <button
                          onClick={() => setDeleteCustomerId(customer.customer_id)}
                          data-testid="delete-customer-button"
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-left text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 shrink-0" />
                          Delete
                        </button>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <button
        onClick={() => {
          setEditingCustomer(null);
          setShowCustomerDialog(true);
        }}
        data-testid="add-customer-button"
        className="fixed bottom-24 right-4 md:right-[calc(50%-12rem)] h-16 w-16 rounded-full bg-[#207024] hover:bg-[#1a5c1d] text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform"
      >
        <Plus className="w-8 h-8" />
      </button>

      <CustomerDialog
        open={showCustomerDialog}
        onOpenChange={setShowCustomerDialog}
        customer={editingCustomer}
        onSuccess={() => {
          refreshCustomers();
          setShowCustomerDialog(false);
          setEditingCustomer(null);
        }}
      />

      <JobDialog
        open={showJobDialog}
        onOpenChange={setShowJobDialog}
        preselectedCustomer={selectedCustomer}
        isAdmin={user?.role === "Admin"}
        onSuccess={() => {
          setShowJobDialog(false);
          setSelectedCustomer(null);
          toast.success("Job created");
        }}
      />

      {/* Reconcile Customer Statuses Dialog */}
      <Dialog open={showReconcile} onOpenChange={setShowReconcile}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="reconcile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-[#207024]" />
              Clean Up Customer Statuses
            </DialogTitle>
            <DialogDescription>
              Reviews "Scheduled" customers and corrects stale statuses.
            </DialogDescription>
          </DialogHeader>

          {reconcileLoading && !reconcilePreview && (
            <div className="py-8 text-center text-zinc-500">Loading preview...</div>
          )}

          {reconcilePreview && (
            <div className="space-y-4">
              <div className="rounded-lg border border-zinc-200 p-3 text-sm">
                <p className="font-medium text-zinc-700 mb-2">Rules</p>
                <ul className="text-xs text-zinc-500 space-y-1 list-disc list-inside">
                  <li>≥ 1 paid invoice → promote to <strong>Customer</strong></li>
                  <li>No paid invoices, no active jobs, inactive &gt; 60 days → demote to <strong>Lead</strong></li>
                  <li>Recent activity or active jobs → keep as <strong>Scheduled</strong></li>
                </ul>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                  <p className="text-2xl font-bold text-green-700">{reconcilePreview.promoted_to_customer_paid}</p>
                  <p className="text-xs text-green-600 font-medium">→ Customer</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
                  <p className="text-2xl font-bold text-blue-700">{reconcilePreview.demoted_to_lead}</p>
                  <p className="text-xs text-blue-600 font-medium">→ Lead</p>
                </div>
                <div className="p-3 rounded-lg bg-zinc-50 border border-zinc-200 text-center">
                  <p className="text-2xl font-bold text-zinc-700">{reconcilePreview.kept_as_scheduled}</p>
                  <p className="text-xs text-zinc-600 font-medium">Stay Scheduled</p>
                </div>
              </div>

              {reconcilePreview.details.promoted.length > 0 && (
                <details className="rounded-lg border border-green-200 bg-green-50/40 p-2">
                  <summary className="cursor-pointer text-sm font-medium text-green-800">
                    {reconcilePreview.details.promoted.length} customers → Customer
                  </summary>
                  <ul className="mt-2 text-xs text-zinc-700 space-y-1 max-h-32 overflow-y-auto">
                    {reconcilePreview.details.promoted.map((c) => (
                      <li key={c.customer_id} className="flex justify-between gap-2">
                        <span className="truncate">{c.name || "Unknown"}</span>
                        <span className="text-zinc-500 flex-shrink-0">{c.paid_invoice_count} paid</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {reconcilePreview.details.demoted.length > 0 && (
                <details className="rounded-lg border border-blue-200 bg-blue-50/40 p-2">
                  <summary className="cursor-pointer text-sm font-medium text-blue-800">
                    {reconcilePreview.details.demoted.length} customers → Lead
                  </summary>
                  <ul className="mt-2 text-xs text-zinc-700 space-y-1 max-h-40 overflow-y-auto">
                    {reconcilePreview.details.demoted.map((c) => (
                      <li key={c.customer_id} className="flex justify-between gap-2">
                        <span className="truncate">{c.name || "Unknown"}</span>
                        <span className="text-zinc-500 flex-shrink-0">
                          {c.last_activity ? new Date(c.last_activity).toLocaleDateString() : "no activity"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {reconcilePreview.scanned === 0 && (
                <p className="text-center text-sm text-zinc-500 py-4">No "Scheduled" customers to review.</p>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowReconcile(false)}
                  disabled={reconcileLoading}
                  className="flex-1 py-3 rounded-lg border-2 border-zinc-200 font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                  data-testid="reconcile-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmReconcile}
                  disabled={reconcileLoading || (reconcilePreview.promoted_to_customer_paid === 0 && reconcilePreview.demoted_to_lead === 0)}
                  className="flex-1 py-3 rounded-lg bg-[#207024] hover:bg-[#185a1c] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="reconcile-confirm"
                >
                  {reconcileLoading ? "Applying..." : "Apply Changes"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Jobs Dialog */}
      <Dialog open={showJobsDialog} onOpenChange={setShowJobsDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#207024]" />
              Jobs for {selectedCustomer?.fullName}
            </DialogTitle>
            <DialogDescription className="sr-only">View customer jobs</DialogDescription>
          </DialogHeader>
          
          {loadingCustomerData ? (
            <div className="py-8 text-center text-zinc-500">Loading...</div>
          ) : customerJobs.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-zinc-500 mb-4">No jobs found for this customer</p>
              <button
                onClick={() => {
                  setShowJobsDialog(false);
                  setShowJobDialog(true);
                }}
                className="px-4 py-2 bg-[#207024] text-white rounded-lg font-semibold hover:bg-[#1a5c1d]"
              >
                + Create New Job
              </button>
            </div>
          ) : (
            <div className="space-y-3 mt-4">
              {customerJobs.map((job) => (
                <div key={job.job_id} className="border rounded-lg p-4 hover:bg-zinc-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold">{job.date || 'No date set'}</div>
                      <div className="text-sm text-zinc-500">{job.time || 'Time TBD'}</div>
                      <div className="text-sm text-zinc-600 mt-1">Tech: {job.assignedEmployee || 'Unassigned'}</div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      job.status === 'Complete' ? 'bg-green-100 text-green-700' :
                      job.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {job.status || 'Open'}
                    </span>
                  </div>
                  {job.notes && <p className="text-sm text-zinc-500 mt-2">{job.notes}</p>}
                </div>
              ))}
              <button
                onClick={() => {
                  setShowJobsDialog(false);
                  setShowJobDialog(true);
                }}
                className="w-full py-2 border-2 border-dashed border-zinc-300 rounded-lg text-zinc-500 hover:border-[#207024] hover:text-[#207024]"
              >
                + Add Another Job
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Estimates Dialog */}
      <Dialog open={showEstimatesDialog} onOpenChange={setShowEstimatesDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#207024]" />
              Estimates for {selectedCustomer?.fullName}
            </DialogTitle>
            <DialogDescription className="sr-only">View customer estimates</DialogDescription>
          </DialogHeader>
          
          {loadingCustomerData ? (
            <div className="py-8 text-center text-zinc-500">Loading...</div>
          ) : customerEstimates.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-zinc-500 mb-4">No estimates found for this customer</p>
              <button
                onClick={() => {
                  setShowEstimatesDialog(false);
                  navigate('/estimates', { 
                    state: { 
                      createEstimate: true, 
                      customer: {
                        customer_id: selectedCustomer.customer_id,
                        fullName: selectedCustomer.fullName,
                        phoneNumber: selectedCustomer.phoneNumber,
                        email: selectedCustomer.email,
                        address: selectedCustomer.address
                      }
                    } 
                  });
                }}
                className="px-4 py-2 bg-[#207024] text-white rounded-lg font-semibold hover:bg-[#1a5c1d]"
              >
                + Create New Estimate
              </button>
            </div>
          ) : (
            <div className="space-y-3 mt-4">
              {customerEstimates.map((est) => (
                <div key={est.lead_id} className="border rounded-lg p-4 hover:bg-zinc-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-[#207024]">
                        ${est.estimate_data?.total?.toFixed(2) || '0.00'}
                      </div>
                      <div className="text-sm text-zinc-500">
                        {new Date(est.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      est.estimate_status === 'converted' ? 'bg-green-100 text-green-700' :
                      est.estimate_status === 'nurturing' ? 'bg-purple-100 text-purple-700' :
                      est.estimate_status === 'scheduled' ? 'bg-cyan-100 text-cyan-700' :
                      est.estimate_status === 'lost' ? 'bg-red-100 text-red-700' :
                      est.estimate_status === 'non_responsive' ? 'bg-orange-100 text-orange-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {est.estimate_status || 'New'}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setShowEstimatesDialog(false);
                      navigate('/estimates', { 
                        state: { viewEstimateId: est.lead_id } 
                      });
                    }}
                    className="text-sm text-[#207024] hover:underline mt-2"
                  >
                    View Details →
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  setShowEstimatesDialog(false);
                  navigate('/estimates', { 
                    state: { 
                      createEstimate: true, 
                      customer: {
                        customer_id: selectedCustomer.customer_id,
                        fullName: selectedCustomer.fullName,
                        phoneNumber: selectedCustomer.phoneNumber,
                        email: selectedCustomer.email,
                        address: selectedCustomer.address
                      }
                    } 
                  });
                }}
                className="w-full py-2 border-2 border-dashed border-zinc-300 rounded-lg text-zinc-500 hover:border-[#207024] hover:text-[#207024]"
              >
                + Add Another Estimate
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Invoices Dialog */}
      <Dialog open={showInvoicesDialog} onOpenChange={setShowInvoicesDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#207024]" />
              Invoices for {selectedCustomer?.fullName}
            </DialogTitle>
            <DialogDescription className="sr-only">View customer invoices</DialogDescription>
          </DialogHeader>
          
          {loadingCustomerData ? (
            <div className="py-8 text-center text-zinc-500">Loading...</div>
          ) : customerInvoices.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-zinc-500 mb-4">No invoices found for this customer</p>
              <button
                onClick={() => {
                  setShowInvoicesDialog(false);
                  navigate('/invoices', { 
                    state: { 
                      createInvoice: true, 
                      customer: {
                        customer_id: selectedCustomer.customer_id,
                        fullName: selectedCustomer.fullName,
                        phoneNumber: selectedCustomer.phoneNumber,
                        email: selectedCustomer.email,
                        address: selectedCustomer.address
                      }
                    } 
                  });
                }}
                className="px-4 py-2 bg-[#207024] text-white rounded-lg font-semibold hover:bg-[#1a5c1d]"
              >
                + Create New Invoice
              </button>
            </div>
          ) : (
            <div className="space-y-3 mt-4">
              {customerInvoices.map((inv) => (
                <div key={inv.invoice_id} className="border rounded-lg p-4 hover:bg-zinc-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-[#207024]">
                        ${inv.total?.toFixed(2) || '0.00'}
                      </div>
                      <div className="text-sm text-zinc-500">
                        {inv.invoice_number || 'No number'}
                      </div>
                      <div className="text-xs text-zinc-400">
                        {new Date(inv.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      inv.paid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {inv.paid ? 'Paid' : 'Unpaid'}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setShowInvoicesDialog(false);
                      navigate('/invoices', { 
                        state: { viewInvoiceId: inv.invoice_id } 
                      });
                    }}
                    className="text-sm text-[#207024] hover:underline mt-2"
                  >
                    View Details →
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  setShowInvoicesDialog(false);
                  navigate('/invoices', { 
                    state: { 
                      createInvoice: true, 
                      customer: {
                        customer_id: selectedCustomer.customer_id,
                        fullName: selectedCustomer.fullName,
                        phoneNumber: selectedCustomer.phoneNumber,
                        email: selectedCustomer.email,
                        address: selectedCustomer.address
                      }
                    } 
                  });
                }}
                className="w-full py-2 border-2 border-dashed border-zinc-300 rounded-lg text-zinc-500 hover:border-[#207024] hover:text-[#207024]"
              >
                + Add Another Invoice
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteCustomerId} onOpenChange={(open) => !open && setDeleteCustomerId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this customer? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-customer">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCustomer} data-testid="confirm-delete-customer" className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Do Not Contact Confirmation */}
      <AlertDialog open={showDNCConfirm} onOpenChange={setShowDNCConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Mark as Do Not Contact?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>This will immediately block <strong>all</strong> communications to this customer:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>SMS notifications</li>
                <li>Email notifications</li>
                <li>Automated follow-ups</li>
                <li>Invoice/Estimate reminders</li>
              </ul>
              <div className="mt-4">
                <label className="text-sm font-medium text-zinc-700">Note (optional)</label>
                <textarea
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  placeholder="Reason for marking as Do Not Contact..."
                  className="w-full mt-1 p-2 border rounded-md text-sm"
                  rows={2}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDNCConfirm(false);
              setStatusChangeCustomer(null);
              setNewStatus("");
              setStatusNote("");
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleStatusChange} className="bg-red-600 hover:bg-red-700">
              Confirm Do Not Contact
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BottomNav />
    </div>
  );
}
